import { useState, useEffect, useRef } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { useForm, Controller } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { z } from 'zod';
import { Building2, MapPin, DollarSign, FileText, Link as LinkIcon, UploadCloud, X, Loader2 } from 'lucide-react';
import { useCreateTransfer, useTransfer, useUpdateTransfer } from '@/hooks/useTransfer';
import dayjs from '@/lib/dayjs';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Textarea } from '@/components/ui/misc';
import { Select } from '@/components/ui/select';
import { cn } from '@/lib/utils';
import { NumberInput } from '@/components/ui/numberInput';
import BusinessSelector from '@/components/common/BusinessSelector';
import AddressSelector from '@/components/common/AddressSelector';
import { useUIStore } from '@/store/uiStore';
import { imageApi } from '@/api/imageApi';
import { FileUploadDropzone } from '@/components/common';

const AREA_UNITS = [
  { value: 'm²', label: 'm²' },
  { value: 'ft²', label: 'ft²' },
];
const CURRENCY = [
  { value: 'VND', label: 'VND' },
  { value: 'USD', label: 'USD' },
];
const LEASE_UNITS = [
  { value: 'year', label: 'Năm' },
  { value: 'month', label: 'Tháng' },
];

const optionalNumber = z.preprocess(
  (val) => {
    if (val === '' || val === undefined || val === null) return undefined;
    if (typeof val === 'string') val = val.replace(/\D/g, '');
    const num = Number(val);
    return isNaN(num) ? undefined : num;
  },
  z.number().optional()
);

const schema = z.object({
  title: z.string({ required_error: 'Bắt buộc nhập' }).min(5, 'Tối thiểu 5 ký tự'),
  description: z.string({ required_error: 'Bắt buộc nhập' }).min(30, 'Tối thiểu 30 ký tự'),
  businessSectorId: z.string({ required_error: 'Bắt buộc nhập' }).min(1, 'Bắt buộc nhập'),
  businessSectorName: z.string().optional(),
  businessTypeId: z.string({ required_error: 'Bắt buộc nhập' }).min(1, 'Bắt buộc nhập'),
  businessTypeName: z.string().optional(),
  propertyType: z.string().optional(),

  streetAddress: z.string({ required_error: 'Bắt buộc nhập' }).min(5, 'Nhập địa chỉ cụ thể'),
  countryId: z.coerce.number().default(1),
  provinceId: z.coerce.number({ required_error: 'Bắt buộc nhập', invalid_type_error: 'Bắt buộc nhập' }).min(1, 'Bắt buộc nhập'),
  districtId: z.coerce.number({ required_error: 'Bắt buộc nhập', invalid_type_error: 'Bắt buộc nhập' }).min(1, 'Bắt buộc nhập'),
  wardId: z.coerce.number({ required_error: 'Bắt buộc nhập', invalid_type_error: 'Bắt buộc nhập' }).min(1, 'Bắt buộc nhập'),

  price: z
    .string({ required_error: 'Bắt buộc nhập' })
    .transform((val) => val.replace(/\D/g, ''))
    .transform((val) => (val ? Number(val) : 0))
    .refine((val) => val > 0, {
      message: 'Bắt buộc nhập',
    }),
  currencyUnit: z.string({ required_error: 'Bắt buộc nhập' }).min(1, 'Bắt buộc nhập').default('VND'),
  revenue: optionalNumber,
  profit: optionalNumber,

  area: optionalNumber,
  areaUnit: z.string().min(1, 'Vui lòng chọn đơn vị diện tích').default('m²'),
  floors: optionalNumber,
  parkingSpaces: optionalNumber,
  seatingCapacity: optionalNumber,
  storageArea: optionalNumber,
  hasStorage: z.boolean().default(false),
  outsideSeatingArea: optionalNumber,
  toiletCount: optionalNumber,
  leaseTerms: z.string().optional(),
  leaseExpiryDate: z.string().optional(),
  leaseDuration: z.coerce.number({ required_error: 'Bắt buộc nhập', invalid_type_error: 'Bắt buộc nhập' }).min(1, 'Bắt buộc nhập'),
  leaseDurationUnit: z.string().min(1, 'Vui lòng chọn đơn vị thời gian').default('year'),
  ndaRequired: z.boolean().default(false),

  tiktokUrl: z.string().url('URL không hợp lệ').optional().or(z.literal('')),
  reelUrl: z.string().url('URL không hợp lệ').optional().or(z.literal('')),
  youtubeShortUrl: z.string().url('URL không hợp lệ').optional().or(z.literal('')),
});
type FormData = z.infer<typeof schema>;

interface CreateTransferPageProps {
  transferId?: string;
  onSuccess?: () => void;
  onCancel?: () => void;
}

interface UploadedImg {
  id: string;
  url: string;
  isPrimary: boolean;
  file?: File;
}

export default function CreateTransferPage({ transferId: propTransferId, onSuccess, onCancel }: CreateTransferPageProps = {}) {
  const params = useParams();
  const id = propTransferId || params.id;
  const isEditMode = !!id;

  const navigate = useNavigate();
  const { mutate: create, isPending: isCreating } = useCreateTransfer();
  const { mutate: update, isPending: isUpdating } = useUpdateTransfer();
  const isPending = isCreating || isUpdating;
  const { addToast } = useUIStore();

  const { data: existingData, isLoading: isLoadingExisting } = useTransfer(id || '');

  const [images, setImages] = useState<UploadedImg[]>([]);
  const [isUploading, setIsUploading] = useState(false);
  const fileInputRef = useRef<HTMLInputElement>(null);

  const {
    register,
    handleSubmit,
    watch,
    setValue,
    reset,
    control,
    formState: { errors },
  } = useForm<FormData>({
    resolver: zodResolver(schema),
    defaultValues: { countryId: 1, currencyUnit: 'VND', areaUnit: 'm²', leaseDurationUnit: 'year' },
  });

  useEffect(() => {
    if (isEditMode && existingData) {
      reset({
        title: existingData.title,
        description: existingData.description,
        businessSectorId: existingData.businessSectorId || '',
        businessSectorName: existingData.businessSectorName || '',
        businessTypeId: existingData.businessTypeId || '',
        businessTypeName: existingData.businessTypeName || '',
        propertyType: existingData.propertyType || '',
        streetAddress: existingData.streetAddress,
        countryId: existingData.countryId || 1,
        provinceId: existingData.provinceId || 1,
        districtId: existingData.districtId || 1,
        wardId: existingData.wardId || 1,
        price: existingData.price ? String(existingData.price) : '',
        currencyUnit: existingData.currencyUnit || 'VND',
        revenue: existingData.revenue ? Number(existingData.revenue) : undefined,
        profit: existingData.profit ? Number(existingData.profit) : undefined,
        ndaRequired: !!(existingData as any).ndaRequired,
        area: existingData.area,
        areaUnit: existingData.areaUnit || 'm²',
        floors: existingData.floors,
        parkingSpaces: existingData.parkingSpaces,
        seatingCapacity: existingData.seatingCapacity,
        storageArea: existingData.storageArea,
        hasStorage: !!existingData.storageArea,
        outsideSeatingArea: existingData.outsideSeatingArea,
        toiletCount: existingData.toiletCount,
        leaseTerms: existingData.leaseTerms || '',
        leaseExpiryDate: existingData.leaseExpiryDate ? dayjs(existingData.leaseExpiryDate).format('YYYY-MM-DD') : '',
        leaseDuration: existingData.leaseDuration,
        leaseDurationUnit: existingData.leaseDurationUnit || 'year',
        tiktokUrl: existingData.tiktokUrl || '',
        reelUrl: existingData.reelUrl || '',
        youtubeShortUrl: existingData.youtubeShortUrl || '',
      } as any);

      if (existingData.images?.length) {
        setImages(
          existingData.images.map((img) => ({
            id: img.imageId || img.id,
            url: img.image?.publicUrl || '',
            isPrimary: img.isPrimary,
          }))
        );
      }
    }
  }, [isEditMode, existingData, reset]);

  const hasStorage = watch('hasStorage');
  const countryId = watch('countryId');
  const provinceId = watch('provinceId');
  const districtId = watch('districtId');
  const wardId = watch('wardId');

  const handleFileUpload = (e: React.ChangeEvent<HTMLInputElement>) => {
    const files = e.target.files;
    if (!files || files.length === 0) return;

    const newLocalImages: UploadedImg[] = [];
    for (let i = 0; i < files.length; i++) {
      const file = files[i];
      if (!file.type.startsWith('image/')) {
        addToast('error', `File ${file.name} không phải là hình ảnh hợp lệ.`);
        continue;
      }

      if (file.size > 1 * 1024 * 1024) {
        addToast('error', `Ảnh ${file.name} vượt quá dung lượng cho phép. Vui lòng chọn ảnh dưới 1MB.`);
        continue;
      }

      newLocalImages.push({
        id: Math.random().toString(36).substring(7),
        url: URL.createObjectURL(file),
        isPrimary: images.length === 0 && i === 0 && newLocalImages.length === 0,
        file,
      });
    }

    setImages((prev) => [...prev, ...newLocalImages]);
    if (fileInputRef.current) fileInputRef.current.value = '';
  };

  const removeImage = (id: string) => {
    setImages((prev) => {
      const filtered = prev.filter((img) => img.id !== id);
      if (filtered.length > 0 && !filtered.find((img) => img.isPrimary)) {
        filtered[0].isPrimary = true;
      }
      return filtered;
    });
  };

  const setPrimaryImage = (id: string) => {
    setImages((prev) => prev.map((img) => ({ ...img, isPrimary: img.id === id })));
  };

  const onSubmit = async (data: FormData) => {
    try {
      setIsUploading(true);
      const finalImages = [];
      const imageIds = [];
      for (const img of images) {
        if (img.file) {
          const res = await imageApi.upload(img.file);
          if (res && res.data) {
            finalImages.push({ id: res.data.id, isPrimary: img.isPrimary });
            imageIds.push(res.data.id);
          }
        } else {
          finalImages.push({ id: img.id, isPrimary: img.isPrimary });
          imageIds.push(img.id);
        }
      }

      const payload = {
        ...data,
        tiktokUrl: data.tiktokUrl || undefined,
        reelUrl: data.reelUrl || undefined,
        youtubeShortUrl: data.youtubeShortUrl || undefined,
        images: finalImages,
        imageIds: imageIds,
      };

      if (isEditMode && id) {
        update({ id, data: payload as any }, {
          onSuccess: () => {
            if (onSuccess) onSuccess();
            else navigate(`/sang-nhuong/${id}`);
          }
        });
      } else {
        create(payload as any, {
          onSuccess: (res: any) => {
            if (onSuccess) onSuccess();
            else navigate(`/sang-nhuong/${res.id}`);
          }
        });
      }
    } catch (err: any) {
      if (err?.response?.status === 413) {
        addToast('error', 'Ảnh tải lên quá lớn (vượt giới hạn 1MB của máy chủ). Vui lòng chọn ảnh nhỏ hơn.');
      } else {
        addToast('error', 'Lỗi khi tải ảnh lên. Vui lòng thử lại.');
      }
    } finally {
      setIsUploading(false);
    }
  };

  const onInvalid = (errors: any) => {
    console.log('Validation Errors:', errors);
    addToast('error', 'Vui lòng kiểm tra lại các trường thông tin bị thiếu hoặc không hợp lệ.');
  };

  return (
    <div className="max-w-3xl mx-auto animate-fade-in pb-10">
      {/* Header */}
      <div className="flex items-center gap-3 mb-8">
        <div>
          <h1 className="font-display font-bold text-3xl" style={{ color: 'var(--text-1)' }}>
            {isEditMode ? 'Cập nhật tin sang nhượng' : 'Đăng tin sang nhượng'}
          </h1>
          <p className="text-base mt-1" style={{ color: 'var(--text-3)' }}>
            Điền đầy đủ thông tin để tăng hiệu quả tìm kiếm
          </p>
        </div>
      </div>

      <form onSubmit={handleSubmit(onSubmit, onInvalid)} className="space-y-6">

        {/* ── Thông tin cơ bản ── */}
        <Card title="Thông tin cơ bản" icon={<FileText size={18} />}>
          <Input label="Tiêu đề *" placeholder="VD: Sang nhượng nhà hàng Nhật 2 tầng Q1" error={errors.title?.message} {...register('title')} />
          <Textarea
            label="Mô tả chi tiết *"
            rows={5}
            placeholder="Mô tả tình trạng kinh doanh, lý do sang nhượng, điểm nổi bật..."
            error={errors.description?.message}
            {...register('description')}
          />
          <BusinessSelector
            required
            sectorId={watch('businessSectorId')}
            typeId={watch('businessTypeId')}
            onSectorChange={(id, name) => {
              setValue('businessSectorId', id);
              setValue('businessSectorName', name);
              setValue('businessTypeId', '');
              setValue('businessTypeName', '');
            }}
            onTypeChange={(id, name) => {
              setValue('businessTypeId', id);
              setValue('businessTypeName', name);
            }}
            errors={{
              sectorId: errors.businessSectorId?.message,
              typeId: errors.businessTypeId?.message,
            }}
          />
        </Card>

        {/* ── Vị trí ── */}
        <Card title="Vị trí" icon={<MapPin size={18} />}>
          <Input label="Địa chỉ cụ thể *" placeholder="123 Nguyễn Huệ, Quận 1" error={errors.streetAddress?.message} {...register('streetAddress')} />
          <AddressSelector
            required
            countryId={countryId}
            provinceId={provinceId}
            districtId={districtId}
            wardId={wardId}
            onCountryChange={(id) => { setValue('countryId', id); setValue('provinceId', 0); setValue('districtId', 0); setValue('wardId', 0); }}
            onProvinceChange={(id) => { setValue('provinceId', id); setValue('districtId', 0); setValue('wardId', 0); }}
            onDistrictChange={(id) => { setValue('districtId', id); setValue('wardId', 0); }}
            onWardChange={(id) => setValue('wardId', id)}
            errors={{
              countryId: errors.countryId?.message,
              provinceId: errors.provinceId?.message,
              districtId: errors.districtId?.message,
              wardId: errors.wardId?.message,
            }}
          />
        </Card>

        {/* ── Tài chính ── */}
        <Card title="Thông tin tài chính" icon={<DollarSign size={18} />}>
          <div className="grid grid-cols-3 gap-5">
            <div className="col-span-2">
              <Controller
                name="price"
                control={control}
                render={({ field: { onChange, onBlur, value, ref } }) => (
                  <NumberInput
                    label="Giá sang nhượng *"
                    placeholder="1.500.000.000"
                    error={errors.price?.message}
                    value={value}
                    onChange={onChange}
                    onBlur={onBlur}
                    ref={ref}
                  />
                )}
              />
            </div>
            <Select label="Đơn vị tiền" options={CURRENCY} {...register('currencyUnit')} value={watch('currencyUnit')} error={errors.currencyUnit?.message} />
          </div>
          <div className="grid grid-cols-2 gap-5">
            <Controller
              name="revenue"
              control={control}
              render={({ field: { onChange, onBlur, value, ref } }) => (
                <NumberInput
                  label="Doanh thu/tháng"
                  placeholder="500.000.000"
                  hint="VD: 500.000.000 = 500tr/tháng"
                  error={errors.revenue?.message}
                  value={value}
                  onChange={onChange}
                  onBlur={onBlur}
                  ref={ref}
                />
              )}
            />
            <Controller
              name="profit"
              control={control}
              render={({ field: { onChange, onBlur, value, ref } }) => (
                <NumberInput
                  label="Lợi nhuận/tháng"
                  placeholder="100.000.000"
                  hint="VD: 100.000.000 = 100tr/tháng"
                  error={errors.profit?.message}
                  value={value}
                  onChange={onChange}
                  onBlur={onBlur}
                  ref={ref}
                />
              )}
            />
          </div>
          <div className="flex items-center gap-3 p-4 rounded-xl border" style={{ borderColor: 'var(--border-c)', background: 'var(--surface-1)' }}>
            <input type="checkbox" id="ndaRequired" className="w-4 h-4 accent-orange-500" {...register('ndaRequired')} />
            <label htmlFor="ndaRequired" className="text-sm cursor-pointer" style={{ color: 'var(--text-2)' }}>
              Yêu cầu NDA (Non-disclosure agreement) trước khi tiết lộ thông tin
            </label>
          </div>
        </Card>

        {/* ── Chi tiết mặt bằng ── */}
        <Card title="Chi tiết mặt bằng" icon={<Building2 size={18} />}>
          <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
            <Input label="Diện tích" type="number" placeholder="150" {...register('area')} error={errors.area?.message} />
            <Select label="Đơn vị" options={AREA_UNITS} {...register('areaUnit')} value={watch('areaUnit')} error={errors.areaUnit?.message} />
            <Input label="Số tầng" type="number" placeholder="2" {...register('floors')} error={errors.floors?.message} />
            <Input label="Sức chứa (chỗ ngồi)" type="number" placeholder="80" {...register('seatingCapacity')} error={errors.seatingCapacity?.message} />
            <Input label="Chỗ đậu xe" type="number" placeholder="5" {...register('parkingSpaces')} error={errors.parkingSpaces?.message} />
            <Input label="Số toilet" type="number" placeholder="4" {...register('toiletCount')} error={errors.toiletCount?.message} />
            <Input label="Khu vực ngoài trời (m²)" type="number" placeholder="30" {...register('outsideSeatingArea')} error={errors.outsideSeatingArea?.message} />
            <div>
              <div className="flex items-center gap-2 mb-2 mt-1">
                <input type="checkbox" id="hasStorage" className="w-4 h-4 accent-orange-500" {...register('hasStorage')} />
                <label htmlFor="hasStorage" className="text-[11px] font-bold uppercase tracking-widest cursor-pointer" style={{ color: 'var(--text-3)' }}>
                  Có kho
                </label>
              </div>
              {hasStorage && <Input type="number" placeholder="Diện tích (m²)" {...register('storageArea')} error={errors.storageArea?.message} />}
            </div>
          </div>
          <Textarea label="Điều khoản thuê" rows={2} placeholder="Ký hợp đồng 5 năm, có thể gia hạn..." {...register('leaseTerms')} error={errors.leaseTerms?.message} />
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            <Input label="Thời hạn thuê *" type="number" placeholder="5" {...register('leaseDuration')} error={errors.leaseDuration?.message} />
            <Select label="Đơn vị *" options={LEASE_UNITS} {...register('leaseDurationUnit')} value={watch('leaseDurationUnit')} error={errors.leaseDurationUnit?.message} />
            <Input label="Ngày hết hạn hợp đồng" type="date" {...register('leaseExpiryDate')} error={errors.leaseExpiryDate?.message} />
          </div>
        </Card>

        {/* ── Media & Mạng xã hội ── */}
        <Card title="Hình ảnh & Truyền thông" icon={<LinkIcon size={18} />}>
          <div className="space-y-4">
            <FileUploadDropzone
              onFileSelect={(files) => handleFileUpload({ target: { files } } as any)}
              isUploading={isUploading}
              multiple
              accept="image/*,.pdf"
              title="Nhấn để chọn hoặc kéo thả ảnh mặt bằng vào đây"
              subtitle="Hỗ trợ JPG, PNG, PDF. Tối đa 5MB"
            />

            {images.length > 0 && (
              <div className="grid grid-cols-2 sm:grid-cols-4 gap-4 mt-4">
                {images.map((img) => (
                  <div key={img.id} className={cn(
                    "relative aspect-video rounded-xl overflow-hidden border-2 group",
                    img.isPrimary ? "border-orange-500 shadow-md" : "border-gray-200"
                  )}>
                    <img src={img.url} alt="" className="w-full h-full object-cover" />

                    <div className="absolute inset-0 bg-black/40 opacity-0 group-hover:opacity-100 transition-opacity flex items-center justify-center gap-2">
                      <button
                        type="button"
                        onClick={() => removeImage(img.id)}
                        className="w-8 h-8 rounded-full bg-white/20 hover:bg-red-500 text-white flex items-center justify-center backdrop-blur-sm transition-colors"
                        title="Xóa ảnh"
                      >
                        <X size={14} />
                      </button>
                    </div>

                    {img.isPrimary ? (
                      <div className="absolute top-2 left-2 px-2 py-1 bg-orange-500 text-white text-[10px] font-bold rounded shadow-sm">
                        Ảnh chính
                      </div>
                    ) : (
                      <button
                        type="button"
                        onClick={() => setPrimaryImage(img.id)}
                        className="absolute top-2 left-2 px-2 py-1 bg-black/50 hover:bg-black/80 text-white text-[10px] font-bold rounded opacity-0 group-hover:opacity-100 transition-opacity"
                      >
                        Đặt làm ảnh chính
                      </button>
                    )}
                  </div>
                ))}
              </div>
            )}
          </div>

          <div className="mt-6 space-y-4 pt-6 border-t" style={{ borderColor: 'var(--border-c)' }}>
            <h3 className="text-sm font-semibold text-gray-700">Liên kết mạng xã hội (Tuỳ chọn)</h3>
            <Input label="TikTok URL" placeholder="https://tiktok.com/@nhahang123" error={errors.tiktokUrl?.message} {...register('tiktokUrl')} />
            <Input label="Instagram Reel URL" placeholder="https://instagram.com/reel/xyz" error={errors.reelUrl?.message} {...register('reelUrl')} />
            <Input label="YouTube Short URL" placeholder="https://youtube.com/shorts/xyz" error={errors.youtubeShortUrl?.message} {...register('youtubeShortUrl')} />
          </div>
        </Card>

        {/* Navigation */}
        <div className="flex gap-4 justify-end pt-4 border-t" style={{ borderColor: 'var(--border-c)' }}>
          <Button type="button" variant="ghost" onClick={() => onCancel ? onCancel() : navigate('/sang-nhuong')} className="px-8 font-bold">
            Hủy
          </Button>
          <Button type="submit" variant="brand" loading={isPending || isUploading} className="px-8 font-bold text-base shadow-lg shadow-orange-500/20">
            {isEditMode ? 'Lưu thay đổi' : 'Đăng tin'}
          </Button>
        </div>
      </form>
    </div>
  );
}

function Card({ title, icon, children }: { title: string; icon: React.ReactNode; children: React.ReactNode }) {
  return (
    <div className="rounded-3xl border p-6 md:p-8 space-y-4 bg-white shadow-sm" style={{ borderColor: 'var(--border-c)' }}>
      <h2 className="font-display font-bold text-lg flex items-center gap-2 pb-4 border-b" style={{ color: 'var(--text-1)', borderColor: 'var(--border-c)' }}>
        <div className="w-8 h-8 rounded-lg bg-orange-50 flex items-center justify-center text-orange-500">
          {icon}
        </div>
        {title}
      </h2>
      <div className="pt-2 space-y-5">
        {children}
      </div>
    </div>
  );
}
